import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Bell,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Circle,
  Columns3,
  Download,
  Dot,
  FileText,
  GaugeCircle,
  Info,
  MessageCircle,
  PlayCircle,
  Send,
  Star,
  Table2,
  Video as VideoIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, PoweredByAdmissionHall } from "@/components/Brand";

const CATEGORY_TONE = {
  urgent: "border-rose-200 bg-rose-50 text-rose-900",
  download: "border-emerald-200 bg-emerald-50 text-emerald-900",
  schedule: "border-amber-200 bg-amber-50 text-amber-900",
  general: "border-slate-200 bg-white text-slate-800",
};

const CATEGORY_ICON = {
  urgent: Bell,
  download: Download,
  schedule: CalendarClock,
  general: Info,
};

const humanDate = (iso) => {
  if (!iso) return "TBA";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const humanDateTime = (iso) => {
  if (!iso) return "TBA";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const QUICK = [
  { to: "/app/allotment", label: "Allotment Data", icon: Table2, hint: "Every published seat, filterable" },
  { to: "/app/closing-ranks", label: "Closing Ranks", icon: GaugeCircle, hint: "Round-wise cutoffs per seat" },
  { to: "/app/compare", label: "Compare Colleges", icon: Columns3, hint: "Side-by-side on any metric" },
  { to: "/app/watchlist", label: "Your Watchlist", icon: Star, hint: "Track your shortlist" },
];

const TimelineDot = ({ status }) => {
  if (status === "done")
    return <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" data-testid="timeline-dot-done" />;
  if (status === "current")
    return (
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center" data-testid="timeline-dot-current">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/60" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500" />
      </span>
    );
  return <Circle className="h-5 w-5 shrink-0 text-slate-300" data-testid="timeline-dot-upcoming" />;
};

const Stat = ({ label, value }) => (
  <div className="border border-slate-200 bg-white p-4">
    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
    <p className="mt-1 font-mono-num text-2xl font-semibold text-[#1A365D]">
      {typeof value === "number" ? value.toLocaleString("en-IN") : value ?? "—"}
    </p>
  </div>
);

const NoticeCard = ({ notice }) => {
  const Icon = CATEGORY_ICON[notice.category] || Info;
  const tone = CATEGORY_TONE[notice.category] || CATEGORY_TONE.general;
  return (
    <div
      data-testid={`notice-${notice.id}`}
      className={`flex flex-col gap-2 border p-4 transition-shadow hover:shadow-sm ${tone}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="text-sm font-semibold leading-snug">{notice.title}</p>
            {notice.body && <p className="mt-1 text-xs leading-relaxed opacity-80">{notice.body}</p>}
          </div>
        </div>
        {notice.is_pinned && (
          <span className="border border-current/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider opacity-70">
            Pinned
          </span>
        )}
      </div>
      {notice.link && (
        <a
          href={notice.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`notice-link-${notice.id}`}
          className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest hover:underline"
        >
          {notice.category === "download" ? "Download" : "Open link"}
          <ArrowUpRight className="h-3 w-3" />
        </a>
      )}
    </div>
  );
};

const VideoCard = ({ video }) => (
  <a
    href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`video-${video.id}`}
    className="group block overflow-hidden border border-slate-200 bg-white transition-transform hover:-translate-y-0.5 hover:shadow-md"
  >
    <div className="relative aspect-video bg-slate-900">
      <img
        src={`https://i.ytimg.com/vi/${video.youtube_id}/hqdefault.jpg`}
        alt={video.title}
        loading="lazy"
        className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <PlayCircle className="h-10 w-10 text-white drop-shadow-md" />
      </div>
    </div>
    <div className="p-3">
      <p className="line-clamp-2 text-sm font-semibold text-[#1A365D]">{video.title}</p>
      {video.description && (
        <p className="mt-1 line-clamp-2 text-xs text-slate-500">{video.description}</p>
      )}
    </div>
  </a>
);

const WebinarCard = ({ item, mode }) => (
  <div
    data-testid={`webinar-${mode}-${item.id}`}
    className="flex flex-col gap-2 border border-slate-200 bg-white p-4"
  >
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
      <VideoIcon className="h-3.5 w-3.5" />
      {mode === "upcoming" ? "Upcoming webinar" : "Past webinar"}
      <Dot className="h-3.5 w-3.5" />
      <span className="text-slate-400">{humanDateTime(item.scheduled_at)}</span>
    </div>
    <p className="text-sm font-semibold text-[#1A365D]">{item.title}</p>
    {item.description && <p className="text-xs text-slate-600">{item.description}</p>}
    <div className="mt-1 flex flex-wrap gap-2">
      {mode === "upcoming" && item.register_url && (
        <a
          href={item.register_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`webinar-register-${item.id}`}
          className="inline-flex items-center gap-1 border border-[#1A365D] bg-[#1A365D] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
        >
          Register <ArrowUpRight className="h-3 w-3" />
        </a>
      )}
      {mode === "past" && item.recording_url && (
        <a
          href={item.recording_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`webinar-recording-${item.id}`}
          className="inline-flex items-center gap-1 border border-slate-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-700 hover:border-[#1A365D] hover:text-[#1A365D]"
        >
          Watch recording <PlayCircle className="h-3 w-3" />
        </a>
      )}
    </div>
  </div>
);

const EmptyBlock = ({ testId, hint }) => (
  <div
    data-testid={testId}
    className="flex items-center justify-center border border-dashed border-slate-300 bg-white p-6 text-center text-xs text-slate-400"
  >
    {hint}
  </div>
);

export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/overview")
      .then(({ data: res }) => {
        if (alive) setData(res);
      })
      .catch((e) => {
        // Overview is a graceful surface — an empty state still renders even
        // when the bundle fails to load. Log for debugging without blocking UX.
        console.debug("Overview bundle load failed", e);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const events = data?.events;
  const timeline = useMemo(() => events || [], [events]);
  const currentEventIdx = useMemo(() => {
    const idx = timeline.findIndex((e) => e.status === "current");
    if (idx >= 0) return idx;
    // fall back to the first upcoming event
    const upIdx = timeline.findIndex((e) => e.status === "upcoming");
    return upIdx >= 0 ? upIdx : -1;
  }, [timeline]);

  if (loading) {
    return (
      <div data-testid="overview-loading" className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">
        Preparing your dashboard…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">
        Could not load the dashboard right now.
      </div>
    );
  }

  const settings = data.settings || {};
  const heroName = user?.name?.split(" ")[0] || "there";
  const headline = settings.hero_headline || `Welcome back, ${heroName}.`;
  const subline =
    settings.hero_subline ||
    "Everything you need for NEET PG counselling — timeline, live cutoffs, and updates from the field.";

  return (
    <div className="space-y-6" data-testid="overview-page">
      {/* HERO ------------------------------------------------------------ */}
      <section className="grid gap-4 border border-slate-200 bg-gradient-to-br from-[#1A365D] to-[#0F2440] p-6 text-white lg:grid-cols-[1.4fr_1fr]">
        <div className="flex flex-col justify-center gap-3">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-amber-300">
            <BrandMark size="sm" /> NeetOne · Counselling Command Centre
          </p>
          <h1 className="font-display text-3xl font-extrabold leading-tight sm:text-4xl">{headline}</h1>
          <p className="max-w-lg text-sm text-blue-100/80">{subline}</p>
          <PoweredByAdmissionHall tone="light" className="mt-1" />
          {data.latest_batch && (
            <div
              data-testid="latest-batch-banner"
              className="mt-1 inline-flex w-fit items-center gap-2 border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-medium text-amber-100"
            >
              <span className="h-2 w-2 rounded-full bg-amber-300" />
              Latest publish · {data.latest_batch.counselling_body} {data.latest_batch.year} {data.latest_batch.round} ·{" "}
              {humanDate(data.latest_batch.published_at)}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 self-stretch sm:grid-cols-2">
          {QUICK.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex flex-col justify-between gap-3 border border-white/10 bg-white/5 p-3 transition-colors hover:border-amber-300/60 hover:bg-white/10"
            >
              <q.icon className="h-5 w-5 text-amber-300" />
              <div>
                <p className="text-sm font-semibold text-white">{q.label}</p>
                <p className="mt-0.5 text-[11px] text-blue-100/70">{q.hint}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STATS ---------------------------------------------------------- */}
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="overview-stats">
        <Stat label="Colleges" value={data.stats.colleges} />
        <Stat label="Allotment rows" value={data.stats.allotments} />
        <Stat label="Closing rank rows" value={data.stats.closing_ranks} />
        <Stat label="Fee records" value={data.stats.fees} />
      </section>

      {/* TIMELINE ------------------------------------------------------- */}
      <section className="space-y-3" data-testid="overview-timeline">
        <header className="flex items-baseline justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-[#1A365D]">Counselling Timeline</h2>
            <p className="text-xs text-slate-500">
              Track every published, current and upcoming step of the counselling season.
            </p>
          </div>
          {currentEventIdx >= 0 && (
            <span className="hidden text-[11px] font-semibold uppercase tracking-widest text-amber-600 sm:inline">
              You are on step {currentEventIdx + 1} of {timeline.length}
            </span>
          )}
        </header>
        {timeline.length === 0 ? (
          <EmptyBlock testId="timeline-empty" hint="Timeline will appear once an admin publishes the schedule." />
        ) : (
          <div className="overflow-x-auto border border-slate-200 bg-white p-4">
            <ol className="flex min-w-max items-start gap-0">
              {timeline.map((ev, i) => (
                <li key={ev.id} className="flex items-start" data-testid={`timeline-${ev.status}-${ev.id}`}>
                  <div className="w-56 shrink-0">
                    <div className="flex items-center gap-2">
                      <TimelineDot status={ev.status} />
                      <span
                        className={`text-[10px] font-bold uppercase tracking-widest ${
                          ev.status === "done"
                            ? "text-emerald-700"
                            : ev.status === "current"
                              ? "text-amber-700"
                              : "text-slate-400"
                        }`}
                      >
                        {ev.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-[#1A365D]">{ev.name}</p>
                    <p className="mt-0.5 font-mono-num text-xs text-slate-500">{humanDate(ev.event_date)}</p>
                    {ev.description && (
                      <p className="mt-1 line-clamp-3 text-xs text-slate-500">{ev.description}</p>
                    )}
                  </div>
                  {i < timeline.length - 1 && (
                    <div
                      className={`mt-2 h-0.5 w-10 shrink-0 ${
                        ev.status === "done" ? "bg-emerald-400" : "bg-slate-200"
                      }`}
                    />
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>

      {/* NOTICES + WEBINARS -------------------------------------------- */}
      <section className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
        <div className="space-y-3" data-testid="overview-notices">
          <header className="flex items-baseline justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-[#1A365D]">Notices &amp; Schedules</h2>
              <p className="text-xs text-slate-500">Downloads, deadlines and important updates.</p>
            </div>
            <FileText className="h-5 w-5 text-slate-400" />
          </header>
          {data.notices.length === 0 ? (
            <EmptyBlock testId="notices-empty" hint="No notices yet. Admin can add downloads and schedules." />
          ) : (
            <div className="grid gap-2">
              {data.notices.map((n) => (
                <NoticeCard key={n.id} notice={n} />
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3" data-testid="overview-webinars">
          <header className="flex items-baseline justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-[#1A365D]">Webinars</h2>
              <p className="text-xs text-slate-500">Register for the next session or catch up on past ones.</p>
            </div>
            <VideoIcon className="h-5 w-5 text-slate-400" />
          </header>
          {data.webinars.upcoming.length === 0 && data.webinars.past.length === 0 ? (
            <EmptyBlock testId="webinars-empty" hint="No webinars scheduled yet." />
          ) : (
            <div className="grid gap-2">
              {data.webinars.upcoming.map((w) => (
                <WebinarCard key={w.id} item={w} mode="upcoming" />
              ))}
              {data.webinars.past.map((w) => (
                <WebinarCard key={w.id} item={w} mode="past" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* VIDEOS --------------------------------------------------------- */}
      <section className="space-y-3" data-testid="overview-videos">
        <header className="flex items-baseline justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-[#1A365D]">Featured videos</h2>
            <p className="text-xs text-slate-500">Curated explainers from the NeetOne YouTube channel.</p>
          </div>
          <PlayCircle className="h-5 w-5 text-slate-400" />
        </header>
        {data.videos.length === 0 ? (
          <EmptyBlock testId="videos-empty" hint="No videos added yet." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.videos.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        )}
      </section>

      {/* COMMUNITY ----------------------------------------------------- */}
      {(settings.telegram_url || settings.whatsapp_url) && (
        <section
          data-testid="overview-community"
          className="grid gap-3 border border-slate-200 bg-white p-5 sm:grid-cols-2"
        >
          <div>
            <h2 className="font-display text-xl font-bold text-[#1A365D]">Join the community</h2>
            <p className="mt-1 text-xs text-slate-500">
              Live discussion with aspirants, mentors and last-year rankers. Fastest way to catch changes.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
            {settings.telegram_url && (
              <a
                href={settings.telegram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="community-telegram"
                className="inline-flex items-center gap-2 border border-sky-300 bg-sky-50 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-sky-800 hover:bg-sky-100"
              >
                <Send className="h-4 w-4" /> Telegram channel
                <ChevronRight className="h-3 w-3" />
              </a>
            )}
            {settings.whatsapp_url && (
              <a
                href={settings.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="community-whatsapp"
                className="inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-emerald-800 hover:bg-emerald-100"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp channel
                <ChevronRight className="h-3 w-3" />
              </a>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
